import { useMemo } from 'react';
import { SeriesOptionsType } from 'highcharts';
import { DispersionChartProps } from '../../../../../../@types/data/types';
import { Space } from 'antd';
import { useTheme } from '../../../../../../providers/theme';
import { calculateMedian } from '../../../../../../components/utils/statistics';
import { getColors } from '../../../../../../components/utils/getColors';
import { useSelected } from '../../../../../../providers/selectedContext';
import { Chart } from '../../../../../../components/chart/BaseChart';
import { ChartSpec } from '../../../../../../components/chart/chartSpec';

const MedianChart: React.FC<DispersionChartProps> = ({ permData, containerWidth, height, xAxisLabel = 'FZI' }) => {
  const { isDay } = useTheme();
  const colors = getColors(isDay);
  const { selectedInfo } = useSelected();

  const chartZones = useMemo(
    () =>
      selectedInfo.project?.correlation?.zones?.map((zone, index) => {
        return {
          ...zone,
          name: 'Rock Type ' + (index + 1)
        };
      }) || [],
    [selectedInfo.project?.correlation?.zones]
  );

  const zonesNames = useMemo(() => chartZones.map(zone => zone.name).concat('No rock type'), [chartZones]);

  const seriesData = useMemo(() => {
    const data = chartZones.map(zone => {
      const pts = permData
        .filter(point => point.xAxisValue >= zone.start && point.xAxisValue <= zone.end)
        .map(point => point.xAxisValue);

      const medianValue = calculateMedian(pts);

      return {
        name: zone.name,
        y: medianValue,
        color: zone.color ?? undefined // evita `null`
      };
    });

    const pointsOutsideZone = permData
      .filter(point => !chartZones.some(zone => point.xAxisValue >= zone.start && point.xAxisValue <= zone.end))
      .map(point => point.xAxisValue);

    const medianOutsideZone = calculateMedian(pointsOutsideZone);

    data.push({
      name: 'No rock type',
      y: medianOutsideZone,
      color: '#808080'
    });

    return data;
  }, [permData, chartZones]);

  const spec: ChartSpec = useMemo(
    () => ({
      type: 'column',
      height,
      title: 'Median of Rock Types',
      xAxis: { categories: zonesNames, crosshair: true },
      yAxis: { title: xAxisLabel, type: 'linear', min: 0 },
      plotOptions: {
        column: {
          pointPadding: 0.2,
          borderWidth: 0
        }
      },
      legend: true,
      series: [
        {
          name: `Median ${xAxisLabel} per Rock Type`,
          type: 'column',
          data: seriesData.map(zone => ({
            name: zone.name,
            y: zone.y,
            color: zone.color ?? undefined
          }))
        }
      ] as SeriesOptionsType[]
    }),
    [zonesNames, seriesData, height, xAxisLabel]
  );

  return (
    <Space
      direction="vertical"
      style={{
        backgroundColor: 'transparent',
        marginTop: '1rem',
        border: `1px solid ${colors.border}`,
        borderRadius: '16px',
        transition: 'width 0.5s',
        width: containerWidth ?? '49%'
      }}
    >
      <div style={{ padding: '0.75rem' }}>
        <Chart spec={spec} />
      </div>
    </Space>
  );
};

export default MedianChart;
